import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/alert';
import { ResizableHandle } from '@art-of-hospitality-solutions-llc/ui-kit/components/resizable';
import { Skeleton } from '@art-of-hospitality-solutions-llc/ui-kit/components/skeleton';
import {
  toastError,
  toastSuccess,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/toast';
import { parseAsString, useQueryState } from 'nuqs';
import { useEffect } from 'react';
import { useParams } from 'react-router-dom';

import Breadcrumbs from '@Components/Breadcrumbs';
import FeaturedImageDialog from '@Components/FeaturedImageDialog';
import { useAuth } from '@Contexts/auth';
import {
  AddBlockBar,
  AddComponentDialog,
  ComponentEditor,
  ConfirmDeleteDialog,
  MainContainer as ContentEditorContainer,
  Header as ContentEditorHeader,
  EditorPane,
  EditorPanel,
  LayerPanel,
  LayerSidebar,
  LayerTree,
  Preview,
  PreviewContainer,
} from '@Components/ContentEditor';
import type { BlockNode, MediaValue } from '@Components/ContentEditor';
import {
  BLOCK_REGISTRY,
  DEFAULT_TOKENS,
  findBlock,
  getAppScreens,
  getBlockLabel,
  getPlaceableChildren,
  getPreviewSkin,
  useActivePage,
  useCanRedo,
  useCanUndo,
  useContentEditorStore,
  useEditorKeyboardShortcuts,
  useSelectionUrlSync,
} from '@Components/ContentEditor';
import { useApps } from '@Hooks/useApps';
import { useAssets } from '@Hooks/useAssets';
import { useContentSlot, useContentSlots } from '@Hooks/useContentSlots';
import { useShowForOptions } from '@Hooks/useShowForOptions';
import { useVenues } from '@Hooks/useVenues';
import {
  useDiscardContentSlotDraft,
  usePublishContentSlot,
  useSaveContentSlotDraft,
} from '@Hooks/useContentSlotMutations';
import { getCloudinaryImageThumbnailUrl } from '@Utils/cloudinary';

/** Fallback when a slot's draft is null (created outside the editor flow). */
const makeEmptyRegionTree = (): BlockNode => ({
  id: 'b_root',
  type: 'region',
  props: {},
  children: [],
});

export default function ContentDetails() {
  // The route param is the core-db App id the slots belong to.
  const { appSlug: appId = '' } = useParams();
  // Route access needs CONTENT_READ (enforced by ProtectedRoute); the
  // workflow actions additionally need CONTENT_WRITE.
  const { hasPermission } = useAuth();
  const canWrite = hasPermission(['CONTENT_WRITE']);
  // The active slot ("page") lives in the URL; unknown/absent falls back to
  // the app's first slot.
  const [slotParam, setSlotParam] = useQueryState('page', parseAsString);

  /* -------------------------------- data --------------------------------- */

  const appsQuery = useApps();
  const app = appsQuery.data?.find((a) => a.id === appId);

  const slotsQuery = useContentSlots({ appId });
  const slots = slotsQuery.data ?? [];
  const activeSlotId =
    slotParam && slots.some((s) => s.id === slotParam)
      ? slotParam
      : slots[0]?.id;

  const slotQuery = useContentSlot({ id: activeSlotId });
  const slot = slotQuery.data;

  /* -------------------------------- store -------------------------------- */

  const openPage = useContentEditorStore((s) => s.openPage);
  const selectedId = useContentEditorStore((s) => s.selectedId);
  const addTargetId = useContentEditorStore((s) => s.addTargetId);
  const pendingDeleteId = useContentEditorStore((s) => s.pendingDeleteId);
  const assetPickerPropName = useContentEditorStore(
    (s) => s.assetPickerPropName,
  );
  const regionAddOpen = useContentEditorStore((s) => s.regionAddOpen);
  const insertType = useContentEditorStore((s) => s.insertType);

  const selectBlock = useContentEditorStore((s) => s.selectBlock);
  const setAddTarget = useContentEditorStore((s) => s.setAddTarget);
  const setPendingDelete = useContentEditorStore((s) => s.setPendingDelete);
  const setAssetPickerProp = useContentEditorStore((s) => s.setAssetPickerProp);
  const openRegionAdd = useContentEditorStore((s) => s.openRegionAdd);
  const closeRegionAdd = useContentEditorStore((s) => s.closeRegionAdd);
  const chooseInsertType = useContentEditorStore((s) => s.chooseInsertType);
  const cancelInsert = useContentEditorStore((s) => s.cancelInsert);
  const updateBlockProp = useContentEditorStore((s) => s.updateBlockProp);
  const updateShowFor = useContentEditorStore((s) => s.updateShowFor);
  const renameBlock = useContentEditorStore((s) => s.renameBlock);
  const toggleHidden = useContentEditorStore((s) => s.toggleHidden);
  const confirmDelete = useContentEditorStore((s) => s.confirmDelete);
  const reorderBlock = useContentEditorStore((s) => s.reorderBlock);
  const duplicateBlock = useContentEditorStore((s) => s.duplicateBlock);
  const addComponent = useContentEditorStore((s) => s.addComponent);
  const insertAt = useContentEditorStore((s) => s.insertAt);
  const undo = useContentEditorStore((s) => s.undo);
  const redo = useContentEditorStore((s) => s.redo);
  const confirmDraftSaved = useContentEditorStore((s) => s.confirmDraftSaved);
  const confirmPublished = useContentEditorStore((s) => s.confirmPublished);
  const confirmDiscarded = useContentEditorStore((s) => s.confirmDiscarded);

  // Activate the fetched slot in the store. The store adopts the server seed
  // unless the local draft has unsaved changes (then local wins). Re-runs on
  // refetch (e.g. after save), which is safe for the same reason.
  useEffect(() => {
    if (!slot) return;
    openPage({
      appSlug: appId,
      pageId: slot.id,
      title: slot.title,
      seed: {
        draft: slot.draft?.root ?? makeEmptyRegionTree(),
        published: slot.published?.root,
        serverUpdatedAt: slot.updatedAt,
        publishedAt: slot.publishedAt ?? undefined,
      },
    });
  }, [slot, appId, openPage]);

  useSelectionUrlSync();
  useEditorKeyboardShortcuts();

  const page = useActivePage();
  const canUndo = useCanUndo();
  const canRedo = useCanRedo();

  /* ----------------------------- mutations ------------------------------- */

  const saveDraftMutation = useSaveContentSlotDraft();
  const publishMutation = usePublishContentSlot();
  const discardMutation = useDiscardContentSlotDraft();
  const isBusy =
    saveDraftMutation.isPending ||
    publishMutation.isPending ||
    discardMutation.isPending;

  /** Persist the draft. Returns false when the save failed (e.g. 409). */
  const saveDraft = async (): Promise<boolean> => {
    if (!page) return false;
    if (!page.hasUnsavedChanges) return true;
    const draftTree = page.draft;
    try {
      const saved = await saveDraftMutation.mutateAsync({
        id: page.pageId,
        payload: {
          draft: { root: draftTree },
          ifUnmodifiedSince: page.serverUpdatedAt,
        },
      });
      confirmDraftSaved(draftTree, saved.updatedAt);
      return true;
    } catch (error) {
      if ((error as { status?: number }).status === 409) {
        toastError(
          'Someone else saved this page since you loaded it. Refresh to load their changes before saving.',
        );
      } else {
        toastError(
          error instanceof Error ? error.message : 'Failed to save draft',
        );
      }
      return false;
    }
  };

  const handleSaveDraft = async () => {
    if (await saveDraft()) toastSuccess('Draft saved');
  };

  // Publishing publishes the SERVER draft, so unsaved local changes are
  // saved first — one click, both steps.
  const handlePublish = async () => {
    if (!page) return;
    const draftTree = page.draft;
    if (!(await saveDraft())) return;
    try {
      const published = await publishMutation.mutateAsync({ id: page.pageId });
      confirmPublished(
        draftTree,
        published.updatedAt,
        published.publishedAt ?? undefined,
      );
      toastSuccess('Published');
    } catch (error) {
      toastError(error instanceof Error ? error.message : 'Failed to publish');
    }
  };

  const handleDiscard = () => {
    if (!page) return;
    discardMutation.mutate(
      { id: page.pageId },
      {
        onSuccess: (updated) => {
          const publishedRoot = updated.published?.root;
          if (publishedRoot) {
            confirmDiscarded(publishedRoot, updated.updatedAt);
          }
          toastSuccess('Draft changes discarded');
        },
        onError: () => toastError('Failed to discard draft'),
      },
    );
  };

  /* ------------------------------- assets -------------------------------- */

  // Image picking reuses the app's featured-image dialog (same one venues
  // use); `asset:` refs resolve against the tenant's real assets.
  const { data: assets } = useAssets();

  // Live options for query-filters fields (the events block's venue picker).
  const { data: venues } = useVenues({ pageSize: 100 }, { staleTime: 60_000 });
  const venueOptions = (venues ?? []).map((venue) => ({
    value: venue.id,
    label: venue.name,
  }));

  // Tag/permission/role options for the Show For gating controls.
  const showForOptions = useShowForOptions();

  const resolveAssetRef = (ref: string) => {
    const asset = assets?.find((a) => `asset:${a.id}` === ref);
    if (!asset) return undefined;
    return {
      id: asset.id,
      // Sized for the largest consumer (preview card media); the media
      // picker's 48px thumb just downscales it.
      url: getCloudinaryImageThumbnailUrl(asset.cloudinaryId, {
        width: 400,
        height: 300,
      }),
      alt: asset.tags?.[0]?.tag.name ?? 'Image',
    };
  };

  /* ------------------------------- derived ------------------------------- */

  const breadcrumbs = (
    <Breadcrumbs
      navList={[
        { name: 'Content Editor', href: '/content-editor' },
        { name: app?.name ?? 'App', href: `/content-editor/${appId}` },
      ]}
    />
  );

  if (slotsQuery.isError || slotQuery.isError) {
    return (
      <div className="flex h-full w-full flex-col">
        {breadcrumbs}
        <Alert variant="destructive">
          <AlertTitle>Unable to load content</AlertTitle>
          <AlertDescription>
            Something went wrong loading this app&apos;s content. Please try
            again.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (slotsQuery.isSuccess && slots.length === 0) {
    return (
      <div className="flex h-full w-full flex-col">
        {breadcrumbs}
        <div className="flex grow items-center justify-center">
          <p className="text-sm text-zinc-500">
            This app has no content slots yet.
          </p>
        </div>
      </div>
    );
  }

  if (!page) {
    return (
      <div className="flex h-full w-full flex-col">
        {breadcrumbs}
        <div className="flex min-h-0 grow flex-col gap-2 rounded-lg border border-gray-200 bg-white p-2">
          <Skeleton className="h-10 w-full" />
          <div className="flex min-h-0 grow gap-2">
            <Skeleton className="h-full w-62.5" />
            <Skeleton className="h-full grow" />
            <Skeleton className="h-full w-92.5" />
          </div>
        </div>
      </div>
    );
  }

  const tree = page.draft;
  const selectedNode = selectedId ? findBlock(tree, selectedId) : undefined;
  const selectedDef = selectedNode
    ? BLOCK_REGISTRY[selectedNode.type]
    : undefined;
  // Same eligibility rules as the layers panel's "+" affordance.
  const canAddToSelected = Boolean(
    selectedNode &&
    selectedDef?.allowedChildren?.length &&
    getPlaceableChildren(selectedNode.type, BLOCK_REGISTRY).length > 0 &&
    !(
      selectedDef.maxChildren !== undefined &&
      selectedNode.children.length >= selectedDef.maxChildren
    ) &&
    selectedNode.children.every((c) => c.locked?.addSiblings !== false),
  );
  const addTarget = addTargetId ? findBlock(tree, addTargetId) : undefined;
  const pendingDeleteBlock = pendingDeleteId
    ? findBlock(tree, pendingDeleteId)
    : undefined;

  const currentPickerAssetId = (() => {
    if (!selectedNode || !assetPickerPropName) return null;
    const media = selectedNode.props[assetPickerPropName] as
      | MediaValue
      | undefined;
    return media?.ref.replace(/^asset:/, '') ?? null;
  })();

  const handleAssetSave = async (assetId: string) => {
    if (!selectedId || !assetPickerPropName) return;
    updateBlockProp(selectedId, assetPickerPropName, {
      ref: `asset:${assetId}`,
    });
    setAssetPickerProp(undefined);
  };

  return (
    <div className="flex h-full w-full flex-col">
      {breadcrumbs}

      <ContentEditorContainer>
        <ContentEditorHeader
          currentPageName={page.title}
          pages={slots.map(({ id, title }) => ({ id, name: title }))}
          handlePageChange={(id) => void setSlotParam(id)}
          isPublished={page.published !== undefined}
          isDirty={page.isDirty}
          hasUnsavedChanges={page.hasUnsavedChanges}
          canUndo={canUndo}
          canRedo={canRedo}
          isBusy={isBusy}
          canWrite={canWrite}
          onUndo={undo}
          onRedo={redo}
          onSaveDraft={handleSaveDraft}
          onPublish={handlePublish}
          onDiscard={handleDiscard}
        />
        <EditorPane>
          <LayerSidebar>
            <LayerPanel
              blockCount={tree.children.length}
              handleInitAdd={openRegionAdd}
            >
              <LayerTree
                root={tree}
                registry={BLOCK_REGISTRY}
                selectedId={selectedId}
                onSelect={selectBlock}
                onToggleHidden={toggleHidden}
                onDelete={setPendingDelete}
                onRename={renameBlock}
                onAddChild={setAddTarget}
                onReorder={reorderBlock}
              />
            </LayerPanel>
          </LayerSidebar>
          <ResizableHandle className="bg-gray-200" />
          <PreviewContainer
            topBar={
              insertType ? (
                <AddBlockBar
                  title={`Add a ${(
                    BLOCK_REGISTRY[insertType]?.displayName ?? 'block'
                  ).toLowerCase()}`}
                  onCancel={cancelInsert}
                />
              ) : undefined
            }
          >
            <Preview
              root={tree}
              registry={BLOCK_REGISTRY}
              tokens={DEFAULT_TOKENS}
              skin={getPreviewSkin(slot?.slotKey)}
              selectedId={selectedId}
              resolveAssetRef={resolveAssetRef}
              insertMode={insertType !== undefined}
              onInsertAt={insertAt}
              onSelect={selectBlock}
              onReorder={reorderBlock}
              onDuplicate={duplicateBlock}
              onDelete={setPendingDelete}
            />
          </PreviewContainer>
          <ResizableHandle className="bg-gray-200" />
          <EditorPanel>
            <ComponentEditor
              node={selectedNode}
              blockDef={selectedDef}
              onPropChange={(propName, value) => {
                if (selectedId) updateBlockProp(selectedId, propName, value);
              }}
              onShowForChange={(rule) => {
                if (selectedId) updateShowFor(selectedId, rule);
              }}
              {...showForOptions}
              onAddComponent={
                canAddToSelected ? () => setAddTarget(selectedId) : undefined
              }
              tokens={DEFAULT_TOKENS}
              screenOptions={getAppScreens(app?.appType)}
              venueOptions={venueOptions}
              resolveAssetRef={resolveAssetRef}
              onOpenAssetLibrary={setAssetPickerProp}
            />
          </EditorPanel>
        </EditorPane>
      </ContentEditorContainer>

      <AddComponentDialog
        open={addTargetId !== undefined}
        onOpenChange={(open) => {
          if (!open) setAddTarget(undefined);
        }}
        registry={BLOCK_REGISTRY}
        allowedTypes={
          addTarget ? getPlaceableChildren(addTarget.type, BLOCK_REGISTRY) : []
        }
        onSelect={addComponent}
      />

      {/* Top-level "Add": pick a region-level component (section/divider),
          then choose its position via the preview's insertion points. */}
      <AddComponentDialog
        open={regionAddOpen}
        onOpenChange={(open) => {
          if (!open) closeRegionAdd();
        }}
        registry={BLOCK_REGISTRY}
        allowedTypes={getPlaceableChildren('region', BLOCK_REGISTRY)}
        onSelect={chooseInsertType}
      />

      <ConfirmDeleteDialog
        open={pendingDeleteId !== undefined}
        onOpenChange={(open) => {
          if (!open) setPendingDelete(undefined);
        }}
        blockLabel={
          pendingDeleteBlock
            ? getBlockLabel(pendingDeleteBlock, BLOCK_REGISTRY)
            : undefined
        }
        onConfirm={confirmDelete}
      />

      <FeaturedImageDialog
        name={
          selectedNode ? getBlockLabel(selectedNode, BLOCK_REGISTRY) : 'card'
        }
        resourceType="card"
        currentFeaturedAssetId={currentPickerAssetId}
        isOpen={assetPickerPropName !== undefined}
        onOpenChange={(open) => {
          if (!open) setAssetPickerProp(undefined);
        }}
        onSave={handleAssetSave}
      />
    </div>
  );
}
